/**
 * Status, rendered the same way everywhere.
 *
 * Form status, submission status and review status were each styled at their own call site, so the
 * same word could arrive in a different colour on two screens and "PUBLISHED" shouted in caps while
 * "Shortlisted" did not. A tone is assigned per meaning — live, resting, ended, needs attention —
 * rather than per screen, and the label is written for a person rather than echoed from the enum.
 */

const TONES = {
  live: "bg-accent/15 text-primary ring-1 ring-inset ring-accent/30",
  resting: "bg-muted text-muted-foreground ring-1 ring-inset ring-border",
  ended: "bg-secondary text-secondary-foreground ring-1 ring-inset ring-border",
  attention: "bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/30",
} as const;

export type StatusTone = keyof typeof TONES;

/** The enum values the API returns, mapped to how a coordinator would say them. */
const KNOWN: Record<string, { label: string; tone: StatusTone }> = {
  DRAFT: { label: "Draft", tone: "resting" },
  PUBLISHED: { label: "Published", tone: "live" },
  ARCHIVED: { label: "Archived", tone: "ended" },
  SUBMITTED: { label: "Submitted", tone: "live" },
  SUPERSEDED: { label: "Superseded", tone: "ended" },
  NEW: { label: "New", tone: "resting" },
  SHORTLISTED: { label: "Shortlisted", tone: "live" },
  REJECTED: { label: "Rejected", tone: "ended" },
  FLAGGED: { label: "Flagged", tone: "attention" },
};

export const StatusPill = ({
  status,
  tone,
  label,
}: {
  status: string;
  /** Overrides the mapping — for a status this component has no opinion about. */
  tone?: StatusTone;
  label?: string;
}): JSX.Element => {
  const known = KNOWN[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        TONES[tone ?? known?.tone ?? "resting"]
      }`}
    >
      {label ?? known?.label ?? status}
    </span>
  );
};
